import { getToken, clearToken } from './auth'

const BASE = import.meta.env.VITE_API_BASE ?? ''

export interface ApiError {
  code: string
  message: string
}

export interface ApiList<T> {
  data: T[]
  meta: { page: number; limit: number; total: number }
}

interface ApiResponse<T> {
  data: T
  meta?: { page: number; limit: number; total: number }
}

function headersFor(body?: unknown): Record<string, string> {
  const headers: Record<string, string> = {}
  // Attach JWT if present (skips /auth/login which runs before a token exists).
  const token = getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  if (import.meta.env.DEV) {
    headers['X-Dev-Bypass'] = '1'
  }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }
  return headers
}

/** Low-level request returning the full envelope (data + meta). */
async function raw<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: headersFor(body),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch (e) {
    throw new Error(
      e instanceof TypeError
        ? '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.'
        : e instanceof Error
          ? e.message
          : '네트워크 오류',
    )
  }

  // 401 — token missing/expired: clear it and force re-login.
  if (res.status === 401 && !path.startsWith('/auth/')) {
    clearToken()
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.')
  }

  // 204 No Content (e.g. successful DELETE) — no JSON body to parse.
  if (res.status === 204) {
    return { data: null as unknown as T } as ApiResponse<T>
  }

  let json: unknown
  try {
    json = await res.json()
  } catch {
    throw new Error(`서버 응답을 파싱할 수 없습니다 (HTTP ${res.status})`)
  }

  if (typeof json === 'object' && json !== null && 'error' in json) {
    const err = (json as { error: ApiError }).error
    throw new Error(err?.message || `오류가 발생했습니다 (${res.status})`)
  }

  return json as ApiResponse<T>
}

/** Request returning only `data`. */
async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  return (await raw<T>(method, path, body)).data
}

export function get<T>(path: string): Promise<T> {
  return api<T>('GET', path)
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return api<T>('POST', path, body)
}

export function put<T>(path: string, body?: unknown): Promise<T> {
  return api<T>('PUT', path, body)
}

export function patch<T>(path: string, body?: unknown): Promise<T> {
  return api<T>('PATCH', path, body)
}

export function del<T>(path: string): Promise<T> {
  return api<T>('DELETE', path)
}

export async function getPage<T>(
  path: string,
  page = 1,
  limit = 20,
): Promise<ApiList<T>> {
  const sep = path.includes('?') ? '&' : '?'
  const res = await raw<T[]>('GET', `${path}${sep}page=${page}&limit=${limit}`)
  return {
    data: res.data,
    meta: res.meta ?? { page, limit, total: res.data.length },
  }
}
